import { useState } from 'react';
import clsx from 'clsx';
import {
  TrendingUp,
  TrendingDown,
  ShieldCheck,
  ShieldOff,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Brain,
} from 'lucide-react';
import { formatOdds, formatMarket } from '../../utils/format';
import { AnimatePresence } from 'framer-motion';
import { FullAnalysisModal } from '../BetCard/FullAnalysisModal';
import type { BetRecommendation } from '../../types';
import type { MLBGame } from '../../types';

interface TopPickCardProps {
  recommendation: BetRecommendation;
  game: MLBGame;
  gameDate: string;
  rank?: number;
}

const DECISION_META: Record<
  string,
  { label: string; cls: string; border: string; pulse: boolean }
> = {
  STRONG_BET: {
    label: 'STRONG PLAY',
    cls: 'text-win bg-win/15 border-win/40',
    border: 'border-win/40',
    pulse: true,
  },
  QUALIFIED_BET: {
    label: 'QUALIFIED',
    cls: 'text-brand-light bg-brand/15 border-brand/30',
    border: 'border-brand/30',
    pulse: false,
  },
  LEAN: {
    label: 'LEAN',
    cls: 'text-warn bg-warn/15 border-warn/30',
    border: 'border-warn/30',
    pulse: false,
  },
  NO_BET: {
    label: 'NO BET',
    cls: 'text-gray-500 bg-surface-3 border-surface-4',
    border: 'border-surface-4',
    pulse: false,
  },
  INVALID: {
    label: 'BLOCKED',
    cls: 'text-loss bg-loss/15 border-loss/30',
    border: 'border-loss/30',
    pulse: false,
  },
};

export function TopPickCard({ recommendation: rec, game, gameDate, rank }: TopPickCardProps) {
  const [showEvidence, setShowEvidence] = useState(false);
  const [showBetModal, setShowBetModal] = useState(false);

  const meta = DECISION_META[rec.decisionStatus] ?? DECISION_META.NO_BET;
  const edgePct = (rec.edge * 100).toFixed(1);
  const edgeCls =
    rec.edge >= 0.07 ? 'text-win' : rec.edge >= 0.04 ? 'text-warn' : 'text-gray-500';
  const confScore = (rec.confidence * 10).toFixed(1);
  const confCls =
    rec.confidence >= 0.7 ? 'text-win' : rec.confidence >= 0.4 ? 'text-warn' : 'text-gray-500';
  const dataScore = Math.round(rec.dataReliabilityScore * 100);
  const validation =
    rec.trustReasonCodes && rec.trustReasonCodes.length > 0 ? 'Flagged' : 'Passed';

  // Build market movement snippet from market agent
  const marketMovement = rec.agentOpinions?.market?.reasoning?.find(
    (r) => r.dataPoint?.startsWith('Steam:') || r.text.toLowerCase().includes('line'),
  );

  // Summary from top reasoning
  const summary =
    rec.reasonsFor[0]?.text ??
    rec.agentOpinions?.matchup?.reasoning?.[0]?.text ??
    rec.agentOpinions?.form?.reasoning?.[0]?.text;

  return (
    <div
      className={clsx(
        'border rounded-xl bg-surface-2 transition-all',
        meta.border,
        showBetModal || showEvidence ? 'shadow-lg' : '',
      )}
    >
      {/* Card header */}
      <div className="flex items-start gap-3 p-3">
        {/* Rank badge */}
        {rank != null && (
          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-surface-3 flex items-center justify-center">
            <span className="text-[9px] font-bold text-gray-500">#{rank}</span>
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {/* Decision chip */}
            <span
              className={clsx(
                'inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-semibold',
                meta.cls,
              )}
            >
              {meta.pulse && (
                <span className="w-1.5 h-1.5 rounded-full bg-win animate-pulse" />
              )}
              {meta.label}
            </span>
            <span className="text-[10px] text-gray-500">
              {formatMarket(rec.market)}
            </span>
          </div>

          {/* Selection + odds */}
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-gray-100 truncate">{rec.selection}</span>
            <span
              className={clsx(
                'text-sm font-mono font-bold flex-shrink-0',
                rec.odds > 0 ? 'text-win' : 'text-gray-300',
              )}
            >
              {formatOdds(rec.odds)}
            </span>
          </div>

          {/* Key metrics row */}
          <div className="flex items-center gap-3 text-[11px] flex-wrap">
            <span className={clsx('font-mono font-bold', edgeCls)}>+{edgePct}% edge</span>
            <span className="text-gray-600">·</span>
            <span className={clsx('font-mono', confCls)}>{confScore} conf</span>
            <span className="text-gray-600">·</span>
            <span className="text-gray-500">{dataScore}% data</span>
            <span className="text-gray-600">·</span>
            <span
              className={clsx(
                'flex items-center gap-0.5',
                validation === 'Passed' ? 'text-win' : 'text-warn',
              )}
            >
              {validation === 'Passed' ? (
                <ShieldCheck className="w-3 h-3" />
              ) : (
                <ShieldOff className="w-3 h-3" />
              )}
              {validation}
            </span>
          </div>

          {/* AI summary */}
          {summary && !showEvidence && (
            <p className="text-[10px] text-gray-500 mt-1.5 leading-relaxed line-clamp-2">
              {summary}
            </p>
          )}

          {/* Market movement signal */}
          {marketMovement && (
            <div className="flex items-center gap-1 mt-1 text-[10px] text-warn">
              <AlertTriangle className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{marketMovement.text}</span>
            </div>
          )}
        </div>

        {/* Stake recommendation */}
        {rec.recommendedStake && (
          <div className="flex-shrink-0 text-right">
            <div className="text-[9px] text-gray-600 uppercase tracking-wide">Suggested</div>
            <div className="text-sm font-mono font-bold text-gray-200">
              ${Math.round(rec.recommendedStake.recommendedAmount)}
            </div>
            <div className="text-[9px] text-gray-600">
              {(rec.recommendedStake.kellyFraction * 100).toFixed(1)}% Kelly
            </div>
          </div>
        )}
      </div>

      {/* Evidence expansion */}
      {showEvidence && (
        <div className="px-3 pb-3 border-t border-surface-4 pt-2 space-y-2 animate-fade-in">
          {/* Agent opinions grid */}
          {rec.agentOpinions && (
            <div className="grid grid-cols-3 gap-1.5">
              {Object.entries(rec.agentOpinions).map(([name, opinion]) => {
                if (!opinion) return null;
                const score = opinion.score ?? 0;
                const scoreCls =
                  score >= 0.65 ? 'text-win' : score <= 0.35 ? 'text-loss' : 'text-warn';
                return (
                  <div
                    key={name}
                    className="bg-surface-0 rounded px-2 py-1.5 border border-surface-4"
                  >
                    <div className="text-[9px] text-gray-600 uppercase tracking-wide capitalize">
                      {name}
                    </div>
                    <div className={clsx('text-xs font-mono font-bold', scoreCls)}>
                      {(score * 100).toFixed(0)}%
                    </div>
                    {opinion.reasoning?.[0] && (
                      <div className="text-[9px] text-gray-600 mt-0.5 leading-tight line-clamp-2">
                        {opinion.reasoning[0].text}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Reasons for/against */}
          <div className="space-y-1">
            {rec.reasonsFor.slice(0, 3).map((r, i) => (
              <div key={i} className="flex items-start gap-1.5 text-[10px] text-gray-400">
                <TrendingUp className="w-3 h-3 text-win flex-shrink-0 mt-px" />
                <span>{r.text}</span>
              </div>
            ))}
            {rec.reasonsAgainst.slice(0, 2).map((r, i) => (
              <div key={i} className="flex items-start gap-1.5 text-[10px] text-gray-500">
                <TrendingDown className="w-3 h-3 text-loss flex-shrink-0 mt-px" />
                <span>{r.text}</span>
              </div>
            ))}
          </div>

          {/* Key risks */}
          {rec.keyRisks && rec.keyRisks.length > 0 && (
            <div className="flex items-start gap-1.5 text-[10px] text-warn/80">
              <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-px" />
              <span>{rec.keyRisks[0]}</span>
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-1 px-3 pb-3 border-t border-surface-4 pt-2">
        <button
          onClick={() => {
            setShowBetModal(true);
            setShowEvidence(false);
          }}
          className={clsx(
            'flex-1 py-1.5 rounded-md text-xs font-semibold transition-all',
            rec.decisionStatus === 'STRONG_BET'
              ? 'bg-win text-surface-0 hover:bg-win-dark'
              : rec.decisionStatus === 'QUALIFIED_BET'
                ? 'bg-brand text-white hover:bg-brand-dark'
                : 'bg-surface-3 text-gray-300 hover:bg-surface-4 border border-surface-4',
          )}
        >
          ⚡ Place Bet
        </button>
        <button
          onClick={() => setShowEvidence(!showEvidence)}
          className="flex items-center gap-1 px-2 py-1.5 rounded-md text-xs text-gray-400 hover:text-gray-200 border border-surface-4 hover:border-surface-3 transition-colors"
        >
          <Brain className="w-3 h-3" />
          {showEvidence ? (
            <>
              Hide <ChevronUp className="w-3 h-3" />
            </>
          ) : (
            <>
              Evidence <ChevronDown className="w-3 h-3" />
            </>
          )}
        </button>
      </div>
      <AnimatePresence>
        {showBetModal && (
          <FullAnalysisModal
            recommendation={rec}
            onClose={() => setShowBetModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
